import { MenuRedesigned as TLMenuRedesigned } from '@treedis/tour-loader';
import { useEffect, useMemo, useState } from 'react';
import { Props } from './types';

/* EXPLANATION OF THE LOGIC IN THIS COMPONENT
 * This component is a wrapper around the MenuRedesigned component from the tour-loader package.
 * Since the menu will overlap most of the popups in the tour (either from Matterport or from Treedis), we need to hide it if it's open and a popup is open and re-show it when the popup is closed. It's not possible to solve this problem with z-indexes. Plus, it's a better UX if the menu is hidden when a popup is open and then re-shown when the popup is closed.
 * To do this, we use MutationObservers to listen for changes in the DOM and check if the menu should be shown or hidden.
 * We have two different MutationObservers, one for Matterport popups and one for Treedis popups.
 * The Matterport MutationObserver is attached to the showcase iframe and listens for changes in the class attribute of the showcase.
 * The Treedis MutationObserver is attached to the .base-tour div and listens for changes in the childList and if the popup is added or removed.
 */

const MP_IFRAME_SELECTOR = '.showcase.showcase';
const TREEDIS_SELECTOR = '.base-tour';

// These a matterport triggers (ex: when a MP Image Tag popup is open)
// Classes
const showcaseTargets = ['modal-open', 'side-panel tool-open'];
// IDs
const baseTourTargets = ['tag-popup', 'treedis-popup-container', 'external-popup', 'vsaction-popup'];

export default function MenuRedesigned(props: Props) {
  const { settings, ...rest } = props;
  const [showcaseNode, setShowcaseNode] = useState<HTMLIFrameElement | null>(null);
  const [showcaseTrigger, setShowcaseTrigger] = useState<string>();
  const [baseTourTrigger, setBaseTourTrigger] = useState<string>();

  const isOpen = useMemo(() => props.open, [props.open]);

  const openMenu = props.onOpen;
  const closeMenu = props.onClose;

  const shouldObserveMutations = useMemo(() => isOpen || showcaseTrigger || baseTourTrigger, [isOpen, showcaseTrigger, baseTourTrigger]);

  const sanitizeSettings = (menuSettings: any) => {
    const clone = { ...menuSettings };
    Object.keys(clone).forEach((key) => {
      if (clone[key] === null) clone[key] = false;
    });

    return clone;
  };

  // Get Showcase Iframe Node
  // Since it may take some time for the showcase to load, we need to keep trying to get the showcase iframe until we get it
  useEffect(() => {
    const getShowcaseIframe = (retryCount = 0) => {
      const node = document.querySelector(MP_IFRAME_SELECTOR);

      if (node) {
        setShowcaseNode(node as HTMLIFrameElement);
      } else if (retryCount < 10) {
        setTimeout(() => getShowcaseIframe(retryCount + 1), 1000);
      }
    };

    getShowcaseIframe();
  }, [shouldObserveMutations]);

  // MP SHOWCASE POPUPS LOGIC TO HIDE BM
  useEffect(() => {
    // Create a new instance of MutationObserver
    const observer = new MutationObserver((mutationsList) => {
      // If we have a currentShowcaseTrigger, then we should check if the menu should be shown
      if (showcaseTrigger) {
        const shouldShowMenu = mutationsList.some((mutation) => showcaseTrigger === mutation.oldValue);

        if (shouldShowMenu) {
          openMenu();
          setShowcaseTrigger(undefined);
        }
      } else {
        // If we don't have a currentShowcaseTrigger, then we should check if the menu should be hidden
        const shouldHideMenu = mutationsList.some((mutation) => {
          // @ts-ignore
          const target: string = mutation.target.className;
          if (showcaseTargets.includes(target)) {
            setShowcaseTrigger(target);
            return true;
          }
          return false;
        });

        if (shouldHideMenu) {
          closeMenu();
        }
      }
    });

    // Start observing the target node and its descendants for DOM changes
    if (showcaseNode && showcaseNode.contentDocument && shouldObserveMutations) {
      observer.observe(showcaseNode.contentDocument, { attributes: true, attributeOldValue: true, subtree: true, attributeFilter: ['class'] });
    } else {
      observer.disconnect();
    }

    return () => {
      // Stop observing when the component unmounts
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showcaseNode, shouldObserveMutations, showcaseTrigger]);

  // TREEDIS POPUPS LOGIC TO HIDE BM
  useEffect(() => {
    const node = document.querySelector(TREEDIS_SELECTOR);
    const observer = new MutationObserver((mutationsList) => {
      // If we have a currentBaseTourTrigger, then we should check if the menu should be shown
      if (baseTourTrigger) {
        // Checking for addedNodes
        // @ts-ignore
        const shouldShowMenu = mutationsList.some((mutation) => baseTourTrigger === mutation.removedNodes?.[0]?.id);

        if (shouldShowMenu) {
          openMenu();
          setBaseTourTrigger(undefined);
        }
      } else {
        // If we don't have a currentBaseTourTrigger, then we should check if the menu should be hidden
        const shouldHideMenu = mutationsList.some((mutation) => {
          // @ts-ignore
          const target: string = mutation.addedNodes?.[0]?.id;

          // Checking for removedNodes
          if (baseTourTargets.includes(target)) {
            setBaseTourTrigger(target);
            return true;
          }
          return false;
        });

        if (shouldHideMenu) {
          closeMenu();
        }
      }
    });

    if (node && shouldObserveMutations) {
      observer.observe(node, { childList: true, subtree: true });
    } else {
      observer.disconnect();
    }

    // Special case for when Panorama is open on start
    // Was used to hide Menu when Panorama was open on start. Not needed anymore.
    // Delete this code in a few months
    // const panoPopup = document.querySelector('#panorama-overlay');
    // if (panoPopup && isOpen) {
    //   setBaseTourTrigger('panorama-overlay');
    //   closeMenu();
    // }

    return () => {
      observer.disconnect();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseTourTrigger, isOpen, shouldObserveMutations]);

  return <TLMenuRedesigned settings={sanitizeSettings(settings)} {...rest} />;
}
